import {
    AppwriteGitHubService,
    GitHubFileSystem,
    GitHubService,
    normalizeRepoPath,
    platformApi,
    type ParsedRepo
} from '@lved/api-gateway';
import {
    collectFileNodes,
    getProjectTemplate,
    GITHUB_HOSTNAME,
    isBinaryFile,
    normalizeWindowsDrivePath,
    wait
} from '@lved/shared-core';
import type { FSNode, ProjectTemplateId } from '@lved/shared-core';
import type { DirectoryData } from '@lved/shared-core/types/window-types';
import { FileChangeSuppressionService } from '@theia-lved-core/browser/platform/file-change-suppression-service';
import { runtimeUrlForProject } from '@theia-lved-core/browser/platform/runtime-url';
import { isE2EAuthBypassBuild } from '@theia-lved-core/common/build-env';
import { FrontendApplicationContribution } from '@theia/core/lib/browser';
import { BinaryBuffer } from '@theia/core/lib/common/buffer';
import type { Disposable } from '@theia/core/lib/common/disposable';
import { MessageService } from '@theia/core/lib/common/message-service';
import type { Progress } from '@theia/core/lib/common/message-service-protocol';
import URI from '@theia/core/lib/common/uri';
import { inject, injectable } from '@theia/core/shared/inversify';
import { EditorManager } from '@theia/editor/lib/browser';
import { FileService } from '@theia/filesystem/lib/browser/file-service';
import type { FileStat } from '@theia/filesystem/lib/common/files';
import { WorkspaceService } from '@theia/workspace/lib/browser/workspace-service';

const ONLINE_PREVIEW_ROOT_PATH: string = '/online-preview';
const MATERIALIZED_WORKSPACE_STORAGE_PREFIX: string = 'theia-browser-workspace:';
const MAX_PARALLEL_FILE_FETCHES: number = 8;
const WORKSPACE_OPEN_TIMEOUT_MS: number = 15_000;
const FILE_CHANGE_SETTLE_DELAY_MS: number = 100;
const UNSAFE_PATH_SEGMENT_CHARACTERS: RegExp = /[^a-zA-Z0-9._-]/g;
const RUNTIME_RESOURCES_ROOT: string = '/@lved-runtime-resources';
const RUNTIME_RESOURCES_MANIFEST_URL: string = `${RUNTIME_RESOURCES_ROOT}/resources-manifest.json`;

type RepoManifestData = {
    path: string;
    children: FSNode[];
};

type MaterializeWorkspaceResult = {
    fileCount: number;
    didUpdateFromRemote: boolean;
};

type RuntimeResourcesManifest = Record<string, FSNode[]>;

type BrowserWorkspaceService = WorkspaceService & {
    closeInCurrentWindow?: () => Promise<void>;
};

const isFileSystemNode = (value: unknown): value is FSNode => {
    if (typeof value !== 'object' || value === null) return false;
    if (!('name' in value) || typeof value.name !== 'string') return false;
    if (!('path' in value) || typeof value.path !== 'string') return false;
    if (!('type' in value) || (value.type !== 'file' && value.type !== 'directory')) return false;
    if (!('children' in value) || value.children === undefined) return true;
    return Array.isArray(value.children) && value.children.every(isFileSystemNode);
};

const isRuntimeResourcesManifest = (value: unknown): value is RuntimeResourcesManifest =>
    typeof value === 'object' &&
    value !== null &&
    Object.values(value).every((nodes: unknown): boolean => Array.isArray(nodes) && nodes.every(isFileSystemNode));

type UiWorkspaceStoresModule = Pick<
    typeof import('@lved/ui'),
    'useFileStore' | 'usePreviewStore' | 'useRecentGithubProjectsStore' | 'useUIStore'
>;

export type BrowserGitHubWorkspaceLoadResult = {
    workspacePath: string;
    fileCount: number;
    repoParam: string;
};

export class PrivateRepositoryLicenseRequiredError extends Error {
    constructor() {
        super(
            'Viewing private repositories online requires an LVGL Pro license, but this repository is not associated with one. Contact us to get access.'
        );
        this.name = 'PrivateRepositoryLicenseRequiredError';
    }
}

@injectable()
// The `BrowserGitHubWorkspaceService` is responsible for loading GitHub repositories into the browser workspace.
// It handles repository validation, workspace materialization, and synchronization with UI stores.
// The service ensures that the workspace is properly set up and ready for use in the Online Preview feature.
export class BrowserGitHubWorkspaceService implements FrontendApplicationContribution {
    @inject(FileService)
    protected readonly fileService!: FileService;

    @inject(WorkspaceService)
    protected readonly workspaceService!: WorkspaceService;

    @inject(EditorManager)
    protected readonly editorManager!: EditorManager;

    @inject(FileChangeSuppressionService)
    protected readonly fileChangeSuppressionService!: FileChangeSuppressionService;

    @inject(MessageService)
    protected readonly messageService!: MessageService;

    protected readonly textEncoder: TextEncoder = new TextEncoder();
    protected syncInProgress: boolean = false;

    onStart(): void {
        GitHubFileSystem.setAuthCallback(() => !isE2EAuthBypassBuild());
    }

    async loadRepository(repoUrl: string): Promise<BrowserGitHubWorkspaceLoadResult> {
        const parsedRepo: ParsedRepo = this.parseRepository(repoUrl);
        await this.validateRepository(parsedRepo);

        const gitHubFileSystem: GitHubFileSystem = GitHubFileSystem.getInstance();
        await gitHubFileSystem.initialize(parsedRepo);

        const workspacePath: string = this.getWorkspacePath(gitHubFileSystem);
        const materializeResult: MaterializeWorkspaceResult = await this.ensureMaterializedWorkspace(
            gitHubFileSystem,
            workspacePath
        );

        const uiModule: UiWorkspaceStoresModule = await this.loadUiStores();
        uiModule.useUIStore.getState().setShowLaunchScreenOnStart(false);
        this.workspaceService.open(URI.fromFilePath(workspacePath), { preserveWindow: true });
        await this.waitForWorkspaceRoot(workspacePath);
        await this.syncUiStores(uiModule, gitHubFileSystem, workspacePath);

        if (materializeResult.didUpdateFromRemote) {
            void this.messageService.info('Project updated to the latest version.');
        }

        return {
            workspacePath,
            fileCount: materializeResult.fileCount,
            repoParam: gitHubFileSystem.repoParam
        };
    }

    async loadBundledTemplate(templateId: ProjectTemplateId): Promise<BrowserGitHubWorkspaceLoadResult> {
        const templatePath: string = getProjectTemplate(templateId).pathSegment.replace(/\\/g, '/');
        const templateUrl: string = `${RUNTIME_RESOURCES_ROOT}/${templatePath}`;
        const manifest: RuntimeResourcesManifest = await this.fetchRuntimeResourcesManifest();
        const children: FSNode[] | undefined = manifest[templateUrl];
        if (!children) throw new Error(`Bundled ${templateId} project is missing from this build.`);

        const fileNodes: FSNode[] = this.collectBundledTemplateFiles(manifest, templateUrl);
        const workspacePath: string = `${ONLINE_PREVIEW_ROOT_PATH}/bundled/${this.sanitizePathSegment(templateId)}`;
        const fileChangeSuppression: Disposable = this.fileChangeSuppressionService.suppressUnder(workspacePath);
        try {
            await this.resetWorkspaceRoot(workspacePath);
            await this.materializeBundledFiles(workspacePath, templateUrl, fileNodes);
            await this.verifyBundledFilesMaterialized(workspacePath, templateUrl, fileNodes);

            const uiModule: UiWorkspaceStoresModule = await this.loadUiStores();
            uiModule.useUIStore.getState().setShowLaunchScreenOnStart(false);
            this.workspaceService.open(URI.fromFilePath(workspacePath), { preserveWindow: true });
            await this.waitForWorkspaceRoot(workspacePath);
            await this.syncBundledUiStores(uiModule, workspacePath, templateId);
            await wait(FILE_CHANGE_SETTLE_DELAY_MS);
        } finally {
            fileChangeSuppression.dispose();
        }

        return { workspacePath, fileCount: fileNodes.length, repoParam: templateId };
    }

    async logout(): Promise<boolean> {
        const uiModule: UiWorkspaceStoresModule = await this.loadUiStores();
        const fileStore = uiModule.useFileStore.getState();
        const canCloseWorkspace: boolean = await fileStore.switchProjectWithUnsavedCheck(null, []);
        if (!canCloseWorkspace) return false;

        await this.editorManager.closeAll();
        fileStore.setDirectoryData([]);
        this.clearOnlinePreviewLocation();

        const { useGitHubAuthStore } = await import('@lved/ui/store/githubAuthStore');
        await useGitHubAuthStore.getState().logout();

        if (this.workspaceService.opened) {
            void this.workspaceService.close();
        }

        return true;
    }

    async closeWorkspaceForLauncher(): Promise<boolean> {
        if (!this.isOnlinePreviewWorkspaceOpen()) {
            this.clearOnlinePreviewLocation();
            return true;
        }

        const uiModule: UiWorkspaceStoresModule = await this.loadUiStores();
        const fileStore = uiModule.useFileStore.getState();
        const canCloseWorkspace: boolean = await fileStore.switchProjectWithUnsavedCheck(null, []);
        if (!canCloseWorkspace) return false;

        await this.editorManager.closeAll();
        fileStore.setDirectoryData([]);
        this.clearOnlinePreviewLocation();
        if (this.workspaceService.opened) await this.closeWorkspace();
        return true;
    }

    isOnlinePreviewWorkspaceOpen(): boolean {
        return Boolean(this.getOpenOnlinePreviewWorkspacePath());
    }

    isGitHubOnlinePreviewWorkspaceOpen(): boolean {
        const workspacePath: string | undefined = this.getOpenOnlinePreviewWorkspacePath();
        return Boolean(workspacePath && !workspacePath.startsWith(`${ONLINE_PREVIEW_ROOT_PATH}/bundled/`));
    }

    // Hash restore skips loadRepository — rehydrate file/preview stores.
    async syncUiStoresForOpenWorkspace(): Promise<void> {
        const workspacePath: string | undefined = this.getOpenOnlinePreviewWorkspacePath();
        if (!workspacePath) {
            throw new Error('No Online Preview workspace is open.');
        }

        const uiModule: UiWorkspaceStoresModule = await this.loadUiStores();
        uiModule.useUIStore.getState().setShowLaunchScreenOnStart(false);

        const bundledPrefix: string = `${ONLINE_PREVIEW_ROOT_PATH}/bundled/`;
        if (workspacePath.startsWith(bundledPrefix)) {
            const templateId: string = workspacePath.slice(bundledPrefix.length);
            await this.syncBundledUiStores(uiModule, workspacePath, templateId as ProjectTemplateId);
            return;
        }

        const repoUrl: string | null =
            GitHubService.getRawRepoParam() || GitHubFileSystem.getInstance().repoParam || null;
        if (!repoUrl) {
            throw new Error('Could not determine the GitHub repository for this workspace.');
        }

        const parsedRepo: ParsedRepo = this.parseRepository(repoUrl);
        await this.validateRepository(parsedRepo);
        const gitHubFileSystem: GitHubFileSystem = GitHubFileSystem.getInstance();
        await gitHubFileSystem.initialize(parsedRepo);
        await this.syncUiStores(uiModule, gitHubFileSystem, workspacePath);
    }

    async syncFromGitHub(): Promise<void> {
        if (this.syncInProgress) {
            void this.messageService.info('Update from GitHub is already in progress…');
            return;
        }
        if (!this.isGitHubOnlinePreviewWorkspaceOpen()) {
            void this.messageService.warn('Open a GitHub project in Online Preview before updating.');
            return;
        }

        const repoUrl: string | null =
            GitHubService.getRawRepoParam() || GitHubFileSystem.getInstance().repoParam || null;
        if (!repoUrl) {
            void this.messageService.error('Could not determine the GitHub repository for this workspace.');
            return;
        }

        this.syncInProgress = true;
        const progress: Progress = await this.messageService.showProgress({
            text: 'Update from GitHub',
            options: { cancelable: false, location: 'notification' }
        });
        try {
            progress.report({ message: 'Fetching latest version…', work: { done: 0, total: 3 } });

            const parsedRepo: ParsedRepo = this.parseRepository(repoUrl);
            await this.validateRepository(parsedRepo);

            const gitHubFileSystem: GitHubFileSystem = GitHubFileSystem.getInstance();
            await gitHubFileSystem.initialize(parsedRepo);

            const workspacePath: string = this.getWorkspacePath(gitHubFileSystem);
            const remoteCommitSha: string = await gitHubFileSystem.getBranchCommitSha();
            const storedCommitSha: string | undefined = this.getStoredCommitSha(workspacePath);
            if (storedCommitSha === remoteCommitSha && (await this.isWorkspaceMaterialized(workspacePath))) {
                const uiModule: UiWorkspaceStoresModule = await this.loadUiStores();
                await this.syncUiStores(uiModule, gitHubFileSystem, workspacePath);
                progress.cancel();
                void this.messageService.info("You're already on the latest version.");
                return;
            }

            progress.report({ message: 'Updating project to the latest version…', work: { done: 1, total: 3 } });
            const uiModule: UiWorkspaceStoresModule = await this.loadUiStores();
            if (!(await uiModule.useFileStore.getState().confirmUnsavedFiles())) {
                progress.cancel();
                return;
            }
            await this.editorManager.closeAll();
            await this.rematerializeWorkspace(gitHubFileSystem, workspacePath, remoteCommitSha);

            progress.report({ message: 'Refreshing workspace…', work: { done: 2, total: 3 } });
            this.workspaceService.open(URI.fromFilePath(workspacePath), { preserveWindow: true });
            await this.waitForWorkspaceRoot(workspacePath);
            await this.syncUiStores(uiModule, gitHubFileSystem, workspacePath);

            progress.cancel();
            void this.messageService.info('Project updated to the latest version.');
        } catch (error: unknown) {
            progress.cancel();
            const message: string = error instanceof Error ? error.message : String(error);
            void this.messageService.error(`Failed to update from GitHub: ${message}`);
        } finally {
            this.syncInProgress = false;
        }
    }

    shouldClearLocationForWorkspaceClose(): boolean {
        const url: URL = new URL(window.location.href);
        const hashPath: string = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
        return (
            this.isOnlinePreviewWorkspaceOpen() ||
            url.searchParams.has('repo') ||
            hashPath.startsWith(ONLINE_PREVIEW_ROOT_PATH)
        );
    }

    clearOnlinePreviewLocation(): void {
        const url: URL = new URL(window.location.href);
        url.searchParams.delete('repo');
        url.hash = '';
        window.history.replaceState({}, document.title, url.toString());
    }

    protected parseRepository(repoUrl: string): ParsedRepo {
        const parsedRepo: ParsedRepo | null = GitHubService.parseRepoFromUrl(repoUrl);
        if (!parsedRepo?.owner || !parsedRepo.repo || !parsedRepo.repoParam) {
            throw new Error('Missing or invalid GitHub repository URL.');
        }

        return parsedRepo;
    }

    protected async validateRepository(parsedRepo: ParsedRepo): Promise<void> {
        if (!parsedRepo.owner || !parsedRepo.repo) {
            throw new Error('Missing GitHub repository owner or name.');
        }

        // E2E auth-bypass bundles skip private-repo validation. E2E tests only load public repos.
        if (isE2EAuthBypassBuild()) return;

        let isPublicRepository: boolean;
        try {
            isPublicRepository = await GitHubService.isPublicRepository(parsedRepo.owner, parsedRepo.repo);
        } catch (error: unknown) {
            throw new Error(error instanceof Error ? error.message : String(error));
        }

        if (isPublicRepository) return;

        const repoIsSupported: boolean = await AppwriteGitHubService.validateRepoSupport(
            parsedRepo.origin ?? GITHUB_HOSTNAME,
            parsedRepo.owner,
            parsedRepo.repo
        );
        if (!repoIsSupported) {
            throw new PrivateRepositoryLicenseRequiredError();
        }
    }

    protected getWorkspacePath(gitHubFileSystem: GitHubFileSystem): string {
        const branchAndPathHash: string = this.hashWorkspaceKey(
            `${gitHubFileSystem.branch}:${gitHubFileSystem.basePath}`
        );
        return [
            ONLINE_PREVIEW_ROOT_PATH,
            this.sanitizePathSegment(gitHubFileSystem.owner),
            this.sanitizePathSegment(gitHubFileSystem.repo),
            branchAndPathHash
        ].join('/');
    }

    protected getOpenOnlinePreviewWorkspacePath(): string | undefined {
        const rootPath: string | undefined = this.workspaceService.tryGetRoots()[0]?.resource.path.toString();
        if (!rootPath) return undefined;
        const normalizedRootPath: string = normalizeWindowsDrivePath(rootPath);
        return normalizedRootPath.startsWith(`${ONLINE_PREVIEW_ROOT_PATH}/`) ? normalizedRootPath : undefined;
    }

    protected async ensureMaterializedWorkspace(
        gitHubFileSystem: GitHubFileSystem,
        workspacePath: string
    ): Promise<MaterializeWorkspaceResult> {
        const progress: Progress = await this.messageService.showProgress({
            text: 'Update from GitHub',
            options: { cancelable: false, location: 'notification' }
        });
        try {
            progress.report({ message: 'Fetching latest version…', work: { done: 0, total: 3 } });
            const remoteCommitSha: string = await gitHubFileSystem.getBranchCommitSha();
            const manifestData: RepoManifestData = await gitHubFileSystem.createManifestData();
            const fileNodes: FSNode[] = collectFileNodes(manifestData.children);
            const wasMaterialized: boolean = await this.isWorkspaceMaterialized(workspacePath);
            const storedCommitSha: string | undefined = this.getStoredCommitSha(workspacePath);

            if (!wasMaterialized || storedCommitSha !== remoteCommitSha) {
                const didUpdateFromRemote: boolean = wasMaterialized;
                progress.report({
                    message: didUpdateFromRemote
                        ? 'Updating project to the latest version…'
                        : 'Pulling latest version from GitHub…',
                    work: { done: 1, total: 3 }
                });
                await this.resetWorkspaceRoot(workspacePath);
                progress.report({ message: 'Writing project files…', work: { done: 2, total: 3 } });
                await this.materializeFiles(gitHubFileSystem, workspacePath, fileNodes);
                this.markWorkspaceMaterialized(workspacePath, remoteCommitSha);
                progress.cancel();
                return { fileCount: fileNodes.length, didUpdateFromRemote };
            }

            const missingFileNodes: FSNode[] = await this.getMissingFileNodes(
                gitHubFileSystem,
                workspacePath,
                fileNodes
            );
            if (missingFileNodes.length > 0) {
                // Marker can survive when OPFS is incomplete — repair missing files only.
                console.debug(
                    `[Online Preview] Repairing browser workspace ${workspacePath}; ${missingFileNodes.length} missing file(s).`
                );
                progress.report({ message: 'Repairing missing files…', work: { done: 2, total: 3 } });
                await this.materializeFiles(gitHubFileSystem, workspacePath, missingFileNodes);
                progress.cancel();
                return {
                    fileCount: await this.countWorkspaceFiles(workspacePath),
                    didUpdateFromRemote: false
                };
            }

            progress.cancel();
            return {
                fileCount: await this.countWorkspaceFiles(workspacePath),
                didUpdateFromRemote: false
            };
        } catch (error: unknown) {
            progress.cancel();
            throw error;
        }
    }

    protected async rematerializeWorkspace(
        gitHubFileSystem: GitHubFileSystem,
        workspacePath: string,
        remoteCommitSha: string
    ): Promise<number> {
        const manifestData: RepoManifestData = await gitHubFileSystem.createManifestData();
        const fileNodes: FSNode[] = collectFileNodes(manifestData.children);
        await this.resetWorkspaceRoot(workspacePath);
        await this.materializeFiles(gitHubFileSystem, workspacePath, fileNodes);
        this.markWorkspaceMaterialized(workspacePath, remoteCommitSha);
        return fileNodes.length;
    }

    protected async materializeFiles(
        gitHubFileSystem: GitHubFileSystem,
        workspacePath: string,
        fileNodes: FSNode[]
    ): Promise<void> {
        await this.runWithConcurrency(fileNodes, MAX_PARALLEL_FILE_FETCHES, async (fileNode: FSNode): Promise<void> => {
            await this.materializeFile(gitHubFileSystem, workspacePath, fileNode);
        });
    }

    protected async fetchRuntimeResourcesManifest(): Promise<RuntimeResourcesManifest> {
        const response: Response = await fetch(RUNTIME_RESOURCES_MANIFEST_URL);
        if (!response.ok) {
            throw new Error(`Failed to load bundled project manifest (${response.status}).`);
        }
        const manifest: unknown = await response.json();
        if (!isRuntimeResourcesManifest(manifest)) throw new Error('Bundled project manifest is invalid.');
        return manifest;
    }

    protected collectBundledTemplateFiles(manifest: RuntimeResourcesManifest, templateUrl: string): FSNode[] {
        const templatePathPrefix: string = `${templateUrl}/`;
        return Object.entries(manifest)
            .filter(
                ([directoryUrl]: [string, FSNode[]]): boolean =>
                    directoryUrl === templateUrl || directoryUrl.startsWith(templatePathPrefix)
            )
            .flatMap(([, nodes]: [string, FSNode[]]): FSNode[] => nodes)
            .filter((node: FSNode): boolean => node.type === 'file' && node.path.startsWith(templatePathPrefix));
    }

    protected async materializeBundledFiles(
        workspacePath: string,
        templateUrl: string,
        fileNodes: FSNode[]
    ): Promise<void> {
        await this.runWithConcurrency(fileNodes, MAX_PARALLEL_FILE_FETCHES, async (fileNode: FSNode): Promise<void> => {
            const relativePath: string = fileNode.path.startsWith(`${templateUrl}/`)
                ? fileNode.path.slice(templateUrl.length + 1)
                : '';
            if (!relativePath) return;

            const response: Response = await fetch(fileNode.path);
            if (!response.ok) throw new Error(`Failed to load bundled project file ${relativePath}.`);

            const localPath: string = `${workspacePath}/${relativePath}`;
            await this.ensureParentFolder(localPath);
            const bytes: Uint8Array = new Uint8Array(await response.arrayBuffer());
            await this.fileService.writeFile(URI.fromFilePath(localPath), BinaryBuffer.wrap(bytes));
        });
    }

    protected async verifyBundledFilesMaterialized(
        workspacePath: string,
        templateUrl: string,
        fileNodes: FSNode[]
    ): Promise<void> {
        const missingFileNodes: FSNode[] = await this.getMissingBundledFileNodes(workspacePath, templateUrl, fileNodes);
        if (missingFileNodes.length === 0) return;

        const firstMissingPath: string = missingFileNodes[0]?.path ?? templateUrl;
        throw new Error(`Bundled project file was not materialized: ${firstMissingPath}`);
    }

    protected async getMissingBundledFileNodes(
        workspacePath: string,
        templateUrl: string,
        fileNodes: FSNode[]
    ): Promise<FSNode[]> {
        const missingFileNodes: FSNode[] = [];
        await this.runWithConcurrency(fileNodes, MAX_PARALLEL_FILE_FETCHES, async (fileNode: FSNode): Promise<void> => {
            const relativePath: string = fileNode.path.startsWith(`${templateUrl}/`)
                ? fileNode.path.slice(templateUrl.length + 1)
                : '';
            if (!relativePath || (await this.fileExists(`${workspacePath}/${relativePath}`))) return;
            missingFileNodes.push(fileNode);
        });
        return missingFileNodes;
    }

    protected async resetWorkspaceRoot(workspacePath: string): Promise<void> {
        const rootUri: URI = URI.fromFilePath(workspacePath);
        const normalizedWorkspacePath: string = normalizeWindowsDrivePath(workspacePath);

        // Don't delete the open workspace root — Theia would fall back to the parent folder.
        if (this.isCurrentWorkspaceRoot(normalizedWorkspacePath)) {
            try {
                const root: FileStat = await this.fileService.resolve(rootUri);
                for (const child of root.children ?? []) {
                    await this.fileService.delete(child.resource, { recursive: true });
                }
                return;
            } catch {
                // Fall through to recreate if the open root cannot be listed.
            }
        }

        try {
            await this.fileService.delete(rootUri, { recursive: true });
        } catch {
            // Missing roots are expected on first load.
        }
        await this.fileService.createFolder(rootUri);
    }

    protected async materializeFile(
        gitHubFileSystem: GitHubFileSystem,
        workspacePath: string,
        fileNode: FSNode
    ): Promise<void> {
        const repoPath: string = normalizeRepoPath(fileNode.path);
        const localRelativePath: string = this.toLocalRelativePath(repoPath, gitHubFileSystem.basePath);
        if (!localRelativePath) return;

        const localPath: string = `${workspacePath}/${localRelativePath}`;
        await this.ensureParentFolder(localPath);

        const bytes: Uint8Array = isBinaryFile(repoPath)
            ? await gitHubFileSystem.getBinaryFileData(repoPath)
            : this.textEncoder.encode(await gitHubFileSystem.getFileData(repoPath));
        await this.fileService.writeFile(URI.fromFilePath(localPath), BinaryBuffer.wrap(bytes));
    }

    protected async getMissingFileNodes(
        gitHubFileSystem: GitHubFileSystem,
        workspacePath: string,
        fileNodes: FSNode[]
    ): Promise<FSNode[]> {
        const missingFileNodes: FSNode[] = [];

        await this.runWithConcurrency(fileNodes, MAX_PARALLEL_FILE_FETCHES, async (fileNode: FSNode): Promise<void> => {
            const repoPath: string = normalizeRepoPath(fileNode.path);
            const localRelativePath: string = this.toLocalRelativePath(repoPath, gitHubFileSystem.basePath);
            if (!localRelativePath) return;

            const localPath: string = `${workspacePath}/${localRelativePath}`;
            const exists: boolean = await this.fileExists(localPath);
            if (!exists) missingFileNodes.push(fileNode);
        });

        return missingFileNodes;
    }

    protected async fileExists(path: string): Promise<boolean> {
        try {
            const stat: FileStat = await this.fileService.resolve(URI.fromFilePath(path));
            return stat.isFile;
        } catch {
            return false;
        }
    }

    protected toLocalRelativePath(repoPath: string, basePath: string): string {
        const normalizedBasePath: string = normalizeRepoPath(basePath);
        const normalizedRepoPath: string = normalizeRepoPath(repoPath);
        const relativePath: string =
            normalizedBasePath && normalizedRepoPath.startsWith(`${normalizedBasePath}/`)
                ? normalizedRepoPath.slice(normalizedBasePath.length + 1)
                : normalizedRepoPath;

        return relativePath
            .split('/')
            .filter((segment: string) => segment && segment !== '.' && segment !== '..')
            .join('/');
    }

    protected async ensureParentFolder(localPath: string): Promise<void> {
        const lastSlashIndex: number = localPath.lastIndexOf('/');
        if (lastSlashIndex <= 0) return;

        const parentPath: string = localPath.slice(0, lastSlashIndex);
        await this.fileService.createFolder(URI.fromFilePath(parentPath));
    }

    protected async countWorkspaceFiles(workspacePath: string): Promise<number> {
        try {
            const root: FileStat = await this.fileService.resolve(URI.fromFilePath(workspacePath));
            return this.countFileStats(root.children ?? []);
        } catch {
            return 0;
        }
    }

    protected countFileStats(stats: FileStat[]): number {
        let count: number = 0;
        for (const stat of stats) {
            if (stat.isFile) {
                count += 1;
                continue;
            }
            count += this.countFileStats(stat.children ?? []);
        }
        return count;
    }

    protected async isWorkspaceMaterialized(workspacePath: string): Promise<boolean> {
        if (localStorage.getItem(`${MATERIALIZED_WORKSPACE_STORAGE_PREFIX}${workspacePath}`) === null) {
            return false;
        }

        try {
            const stat: FileStat = await this.fileService.resolve(URI.fromFilePath(workspacePath));
            return stat.isDirectory;
        } catch {
            return false;
        }
    }

    protected getStoredCommitSha(workspacePath: string): string | undefined {
        const value: string | null = localStorage.getItem(`${MATERIALIZED_WORKSPACE_STORAGE_PREFIX}${workspacePath}`);
        if (!value || value === '1') return undefined;
        return value;
    }

    protected markWorkspaceMaterialized(workspacePath: string, commitSha: string): void {
        localStorage.setItem(`${MATERIALIZED_WORKSPACE_STORAGE_PREFIX}${workspacePath}`, commitSha);
    }

    protected async syncUiStores(
        uiModule: UiWorkspaceStoresModule,
        gitHubFileSystem: GitHubFileSystem,
        workspacePath: string
    ): Promise<void> {
        const directoryData: DirectoryData | null = await platformApi.getDirectoryData(workspacePath);
        if (!directoryData) throw new Error(`Failed to read materialized workspace ${workspacePath}`);

        const switched: boolean = await uiModule.useFileStore
            .getState()
            .switchProjectWithUnsavedCheck(workspacePath, directoryData.files, { validateLicense: false });
        if (!switched) throw new Error(`Failed to switch to materialized workspace ${workspacePath}`);

        uiModule.usePreviewStore.getState().setRuntimeScriptPath(runtimeUrlForProject(workspacePath));
        uiModule.useFileStore.getState().setPreviewUrlProject(gitHubFileSystem.repo);
        uiModule.useRecentGithubProjectsStore.getState().addProject(gitHubFileSystem.repoParam);
    }

    protected async syncBundledUiStores(
        uiModule: UiWorkspaceStoresModule,
        workspacePath: string,
        templateId: ProjectTemplateId
    ): Promise<void> {
        const directoryData: DirectoryData | null = await platformApi.getDirectoryData(workspacePath);
        if (!directoryData) throw new Error(`Failed to read bundled workspace ${workspacePath}`);

        const switched: boolean = await uiModule.useFileStore
            .getState()
            .switchProjectWithUnsavedCheck(workspacePath, directoryData.files, { validateLicense: false });
        if (!switched) throw new Error(`Failed to switch to bundled workspace ${workspacePath}`);

        uiModule.usePreviewStore.getState().setRuntimeScriptPath(runtimeUrlForProject(workspacePath));
        uiModule.useFileStore.getState().setPreviewUrlProject(templateId);
    }

    // Waits for the workspace root to be set in the WorkspaceService after opening a new workspace.
    protected async waitForWorkspaceRoot(workspacePath: string): Promise<void> {
        const normalizedWorkspacePath: string = normalizeWindowsDrivePath(workspacePath);
        if (this.isCurrentWorkspaceRoot(normalizedWorkspacePath)) return;

        await new Promise<void>((resolve: () => void, reject: (error: Error) => void): void => {
            let disposable: Disposable | undefined;
            const timeout: number = window.setTimeout((): void => {
                disposable?.dispose();
                reject(new Error(`Timed out waiting for workspace root ${normalizedWorkspacePath}`));
            }, WORKSPACE_OPEN_TIMEOUT_MS);

            disposable = this.workspaceService.onWorkspaceChanged((): void => {
                if (!this.isCurrentWorkspaceRoot(normalizedWorkspacePath)) return;
                window.clearTimeout(timeout);
                disposable?.dispose();
                resolve();
            }) as Disposable;
        });
    }

    protected isCurrentWorkspaceRoot(workspacePath: string): boolean {
        const currentRootPath: string | undefined = this.workspaceService.tryGetRoots()[0]?.resource.path.toString();
        return Boolean(currentRootPath && normalizeWindowsDrivePath(currentRootPath) === workspacePath);
    }

    protected async loadUiStores(): Promise<UiWorkspaceStoresModule> {
        return (await import('@lved/ui')) as unknown as UiWorkspaceStoresModule;
    }

    protected async closeWorkspace(): Promise<void> {
        const workspaceService: BrowserWorkspaceService = this.workspaceService;
        if (workspaceService.closeInCurrentWindow) {
            await workspaceService.closeInCurrentWindow();
            return;
        }
        await workspaceService.close();
    }

    protected async runWithConcurrency<T>(
        items: T[],
        concurrency: number,
        worker: (item: T) => Promise<void>
    ): Promise<void> {
        let nextIndex: number = 0;
        const workerCount: number = Math.min(concurrency, items.length);

        const runWorker = async (): Promise<void> => {
            while (nextIndex < items.length) {
                const item: T = items[nextIndex];
                nextIndex += 1;
                await worker(item);
            }
        };

        await Promise.all(Array.from({ length: workerCount }, runWorker));
    }

    protected sanitizePathSegment(segment: string): string {
        return segment.replace(UNSAFE_PATH_SEGMENT_CHARACTERS, '_');
    }

    protected hashWorkspaceKey(value: string): string {
        let hash: number = 5381;
        for (let index: number = 0; index < value.length; index += 1) {
            hash = (hash * 33) ^ value.charCodeAt(index);
        }
        return (hash >>> 0).toString(16);
    }
}
